/**
 * Automerge Repository Configuration
 *
 * This file initializes the Automerge repo with IndexedDB persistence
 * and Supabase Realtime network sync.
 *
 * Phase 3: Local-only with IndexedDB storage
 * Phase 6: Added Supabase Realtime network sync
 */

import type { Repo, PeerId } from '@automerge/automerge-repo'

let repoInstance: Repo | null = null

/**
 * Get or create the global Automerge repository instance.
 * Lazy initialization ensures repo is only created on the client.
 *
 * Features:
 * - IndexedDB storage for offline-first persistence
 * - Supabase Realtime sync for multi-device collaboration
 * - Persistent peer ID for consistent identity across sessions
 *
 * IMPORTANT: This function must only be called in client components.
 *
 * The `typeof window` guard and the `await import()` below are load-bearing together: Automerge
 * loads WASM, and a top-level import would evaluate it during SSR and the build. Do not hoist
 * these imports to module scope, and do not drop the guard.
 */
export async function getRepo(): Promise<Repo> {
  if (typeof window === 'undefined') {
    throw new Error(
      'Automerge repo can only be initialized in browser environment',
    )
  }

  if (!repoInstance) {
    const { Repo } = await import('@automerge/automerge-repo')
    const { IndexedDBStorageAdapter } =
      await import('@automerge/automerge-repo-storage-indexeddb')
    const { getSupabaseSyncClient } = await import('./supabase-sync')
    const { SupabaseNetworkAdapter } = await import('./SupabaseNetworkAdapter')

    // Generate unique peer ID per tab/session
    // Each browser tab gets its own peer ID for cross-tab sync
    // For cross-device sync, user authentication will provide identity
    const peerId = crypto.randomUUID()

    // Initialize Supabase sync client
    const syncClient = getSupabaseSyncClient()

    // Create network adapter with Supabase Realtime
    const networkAdapter = new SupabaseNetworkAdapter(syncClient)

    // Create repo with network sync
    // The Repo will automatically call adapter.connect() with the peerId
    repoInstance = new Repo({
      storage: new IndexedDBStorageAdapter(),
      network: [networkAdapter],
      peerId: peerId as PeerId,
      isEphemeral: true, // Don't persist sync state for Supabase peers
    })

    // Wait for network adapter to be fully connected
    // Add timeout to prevent hanging indefinitely if Realtime connection fails
    try {
      await Promise.race([
        networkAdapter.whenReady(),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('Network adapter connection timeout')),
            5000,
          ),
        ),
      ])
      console.log('✅ Network adapter ready')
    } catch (error) {
      console.warn('Network adapter failed to connect:', error)
      // Continue anyway - offline-first should still work
    }
  }
  return repoInstance
}
